import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {UmpireMatch} from '@tisa-group/models';
import {ImageVariantModule} from '@tisa-group/core/pipes/image-variant';
import {SvgIconComponent} from '@tisa-group/client-ui/components/svg-icon';

@Component({
  selector: 'tisa-match-tile',
  imports: [
    CommonModule,
    ImageVariantModule,
    SvgIconComponent,
    RouterLink,
    TranslateModule,
  ],
  templateUrl: './match-tile.component.html',
  styleUrl: './match-tile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchTileComponent {
  private translateService = inject(TranslateService);

  match = input<UmpireMatch>();

  homeSquadLogoError = signal(false);

  awaySquadLogoError = signal(false);

  private readonly matchCenterLaunchDate = new Date('2024-12-15T00:00:00');

  locale = computed(() => this.translateService.currentLang || 'pl');

  showMatchCenterLink = computed(() => {
    const currentMatch = this.match();
    if (!currentMatch?.slug || !currentMatch?.start_time) {
      return false;
    }
    const matchStartTime = new Date(currentMatch.start_time);
    if (matchStartTime < this.matchCenterLaunchDate) {
      return false;
    }

    // Show match center link only 2 hours before match starts
    const now = new Date();
    const twoHoursBeforeMatch = new Date(matchStartTime.getTime() - 2 * 60 * 60 * 1000);
    return now >= twoHoursBeforeMatch;
  });

  showTicketsLink = computed(() => {
    const currentMatch = this.match();
    if (!currentMatch?.ticket_counter?.external_url) {
      return false;
    }
    if (currentMatch.status !== 'pending') {
      return false;
    }
    if (!currentMatch.start_time) {
      return false;
    }
    const matchStartTime = new Date(currentMatch.start_time);
    const now = new Date();
    return matchStartTime > now;
  });

  showHighlightsLink = computed(() => {
    const currentMatch = this.match();
    return !!currentMatch?.highlights_url;
  });

  matchCenterUrl = computed(() => {
    const slug = this.match()?.slug;
    return slug ? ['/', this.locale(), 'match-center', slug] : null;
  });

  ticketsUrl = computed(() => {
    return this.match()?.ticket_counter?.external_url || null;
  });

  highlightsUrl = computed(() => {
    return this.match()?.highlights_url || null;
  });

  showAnyButton = computed(() => {
    return (
      this.showMatchCenterLink() || this.showTicketsLink() || this.showHighlightsLink()
    );
  });
}
